import { useCallback, useState } from 'react';

/**
 * useGeolocation — thin wrapper around navigator.geolocation for POS use cases.
 *
 * `request({ highAccuracy, timeout })` returns a Promise that resolves to
 *   { latitude, longitude, accuracy_m } or rejects with an Error whose `code`
 *   is one of: 'unsupported' | 'permission_denied' | 'position_unavailable'
 *              | 'timeout' | 'insecure_context'.
 *
 * Notes for iOS/PWA:
 *  - iOS Safari requires HTTPS. Preview + prod are HTTPS ✔.
 *  - First-time request MUST be triggered by a user gesture (button click).
 *    Do not call from a bare useEffect on mount for iOS-first users.
 *  - PWA installed via "Add to Homescreen" inherits Safari's permission grant.
 */
const useGeolocation = () => {
    const [state, setState] = useState({ status: 'idle', coords: null, error: null });

    const request = useCallback(({ highAccuracy = true, timeout = 8000, maximumAge = 30000 } = {}) => {
        return new Promise((resolve, reject) => {
            if (!('geolocation' in navigator)) {
                const err = new Error('GPS no soportado por este navegador');
                err.code = 'unsupported';
                setState({ status: 'error', coords: null, error: err });
                return reject(err);
            }
            // Some Android in-app browsers block geolocation on non-secure contexts
            if (typeof window !== 'undefined' && window.isSecureContext === false) {
                const err = new Error('GPS requiere HTTPS');
                err.code = 'insecure_context';
                setState({ status: 'error', coords: null, error: err });
                return reject(err);
            }

            setState((s) => ({ ...s, status: 'requesting', error: null }));
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const coords = {
                        latitude: pos.coords.latitude,
                        longitude: pos.coords.longitude,
                        accuracy_m: pos.coords.accuracy,
                    };
                    setState({ status: 'success', coords, error: null });
                    resolve(coords);
                },
                (err) => {
                    // Map W3C PositionError codes to our friendly codes
                    const map = {
                        1: 'permission_denied',
                        2: 'position_unavailable',
                        3: 'timeout',
                    };
                    const friendly = new Error(err.message || 'No se pudo obtener la ubicación');
                    friendly.code = map[err.code] || 'position_unavailable';
                    setState({ status: 'error', coords: null, error: friendly });
                    reject(friendly);
                },
                { enableHighAccuracy: highAccuracy, timeout, maximumAge }
            );
        });
    }, []);

    return { ...state, request };
};

export default useGeolocation;
